import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
    FaPlus, FaEdit, FaTrash, FaTimes, FaSearch,
    FaChevronLeft, FaChevronRight,
    FaShieldAlt,
} from "react-icons/fa";
import { MdInventory2 } from "react-icons/md";
import { BsBuilding } from "react-icons/bs";
import { getUserRole } from "../../helpers/local_storage_helpers";
import SearchableSelect from "../../components/inputFields/SearchableSelect";
import {
    getAssets,
    createAsset,
    updateAsset,
    deleteAsset,
    getAssetSummary,
    getAssetLookups,
} from "../../services/fpoAsset";

// ─────────────────────────────────────────────
// Role helpers
// ─────────────────────────────────────────────
const useRole = () => {
    const role = getUserRole();
    return {
        role,
        canWrite: ["fpo_admin", "manager", "data_entry", "field_staff"].includes(role) || !role,
        canRead:  true,
        isAdmin:  role === "fpo_admin" || role === "manager" || !role,
        isDataEntry: role === "data_entry" || role === "field_staff",
    };
};

// ─────────────────────────────────────────────
// Formatting helpers
// ─────────────────────────────────────────────
const fmtMoney = (val) => {
    if (val == null || val === "") return "—";
    const n = parseFloat(val);
    if (isNaN(n)) return "—";
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    return `₹${n.toLocaleString("en-IN")}`;
};




// ─────────────────────────────────────────────
// Modal shell (matches Procurement style exactly)
// ─────────────────────────────────────────────
const Modal = ({ title, subtitle, onClose, children, wide }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
    >
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            onClick={(e) => e.stopPropagation()}
            className={`bg-[#f5f0e8] rounded-2xl shadow-2xl w-full ${wide ? "max-w-xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}
        >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
                <div>
                    <h3 className="font-bold text-gray-900 text-base">{title}</h3>
                    {subtitle && <p className="text-gray-500 text-xs mt-0.5">{subtitle}</p>}
                </div>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-lg transition-colors ml-4 flex-shrink-0"
                >
                    <FaTimes size={14} />
                </button>
            </div>
            <div className="px-6 pb-6">{children}</div>
        </motion.div>
    </motion.div>
);

// ─────────────────────────────────────────────
// Pagination
// ─────────────────────────────────────────────
const Pagination = ({ page, total, pageSize, onPage }) => {
    const pages = Math.ceil(total / pageSize) || 1;
    if (pages <= 1) return null;
    return (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
            <span className="text-xs text-gray-400">
                Showing {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}
            </span>
            <div className="flex items-center gap-1">
                <button
                    disabled={page <= 1}
                    onClick={() => onPage(page - 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <FaChevronLeft size={10} />
                </button>
                <span className="text-xs text-gray-600 px-2">{page} / {pages}</span>
                <button
                    disabled={page >= pages}
                    onClick={() => onPage(page + 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <FaChevronRight size={10} />
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// Loading / Empty states
// ─────────────────────────────────────────────
const Spinner = ({ label = "Loading…" }) => (
    <div className="flex flex-col items-center justify-center h-48 gap-3">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">{label}</p>
    </div>
);

const Empty = ({ label }) => (
    <div className="flex flex-col items-center justify-center h-48 gap-3 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
            <MdInventory2 className="text-gray-300 text-2xl" />
        </div>
        <p className="font-semibold text-gray-400 text-sm">{label}</p>
    </div>
);

const ErrState = ({ msg, onRetry }) => (
    <div className="flex flex-col items-center justify-center h-48 gap-3">
        <p className="text-red-500 font-medium text-sm">{msg}</p>
        {onRetry && (
            <button onClick={onRetry} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold">
                Retry
            </button>
        )}
    </div>
);

// ─────────────────────────────────────────────
// Condition pill
// ─────────────────────────────────────────────
const CONDITION_STYLES = {
    working:      "bg-green-50 text-green-700 border border-green-200",
    under_repair: "bg-amber-50 text-amber-700 border border-amber-200",
    disposed:     "bg-red-50 text-red-500 border border-red-200",
};
const CONDITION_LABELS = {
    working: "Working",
    under_repair: "Under Repair",
    disposed: "Disposed",
};

const ConditionPill = ({ condition }) => {
    if (!condition) return <span className="text-gray-400 text-xs">—</span>;
    const c = condition.toLowerCase();
    return (
        <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${CONDITION_STYLES[c] || "bg-gray-100 text-gray-500"}`}>
            {CONDITION_LABELS[c] || condition}
        </span>
    );
};

// ─────────────────────────────────────────────
// Allocation pill
// ─────────────────────────────────────────────
const AllocBadge = ({ row }) => {
    const t = row.allocated_type;
    if (t === "unallocated" || !t) return <span className="text-gray-400 text-xs">Unallocated</span>;
    if (t === "office") return (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
            <BsBuilding size={9} /> Office / Common
        </span>
    );
    const name = row.allocated_to_name || (row.allocated_to?.name) || "—";
    const initials = name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
    const isStaff = t === "staff";
    return (
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border ${isStaff ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-purple-50 text-purple-700 border-purple-200"}`}>
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${isStaff ? "bg-indigo-200 text-indigo-800" : "bg-purple-200 text-purple-800"}`}>{initials}</span>
            {name}
            <span className="opacity-60">· {isStaff ? "staff" : "member"}</span>
        </span>
    );
};

// ─────────────────────────────────────────────
// Category icon map
// ─────────────────────────────────────────────
const CATEGORY_ICONS = {
    office:      "🖥️",
    machinery:   "⚙️",
    vehicle:     "🚛",
    furniture:   "🪑",
    electronics: "💻",
    other:       "📦",
};

// ─────────────────────────────────────────────
// DELETE CONFIRM MODAL
// ─────────────────────────────────────────────
const DeleteModal = ({ asset, onClose, onDeleted }) => {
    const [saving, setSaving] = useState(false);

    const handleDelete = async () => {
        setSaving(true);
        try {
            await deleteAsset(asset.id);
            toast.success(`${asset.asset_code} decommissioned`);
            onDeleted();
            onClose();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.response?.data?.message || "Delete failed.";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title="Decommission asset?" subtitle={`This will soft-delete ${asset.asset_code} — it won't appear in the active register but is retained for history.`} onClose={onClose}>
            <div className="flex flex-col gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                    <p className="text-amber-700 text-xs font-semibold">{asset.asset_code} — {asset.name}</p>
                    <p className="text-amber-600 text-xs mt-0.5">{asset.category_display} · {asset.condition}</p>
                </div>
                <div className="flex gap-3 pt-2 border-t border-gray-200">
                    <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-300 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors">
                        Cancel
                    </button>
                    <button type="button" onClick={handleDelete} disabled={saving}
                        className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-60">
                        {saving ? "Removing…" : "Decommission"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────
// ASSET FORM MODAL
// ─────────────────────────────────────────────
const CATEGORY_OPTIONS = [
    { value: "office",      label: "Office" },
    { value: "machinery",   label: "Machinery" },
    { value: "vehicle",     label: "Vehicles" },
    { value: "furniture",   label: "Furniture" },
    { value: "electronics", label: "Electronics" },
    { value: "other",       label: "Other (custom)" },
];
const CONDITION_OPTIONS = [
    { value: "working",      label: "Working" },
    { value: "under_repair", label: "Under Repair" },
    { value: "disposed",     label: "Disposed" },
];
const ALLOCATION_TABS = [
    { value: "unallocated", label: "Unallocated" },
    { value: "staff",       label: "Staff" },
    { value: "member",      label: "Member" },
    { value: "office",      label: "Office / Common" },
];

const EMPTY_FORM = {
    name:             "",
    category:         "office",
    custom_category:  "",
    serial_number:    "",
    quantity:         "1",
    purchase_price:   "",
    purchase_date:    "",
    warranty_till:    "",
    vendor:           "",
    warehouse:        "",
    allocated_type:   "unallocated",
    allocated_staff:  "",
    allocated_member: "",
    allocated_at:     "",
    condition:        "working",
    notes:            "",
    invoice_file:     null,
    image:            null,
};

const AssetFormModal = ({ editing, lookups, onClose, onSaved, isDataEntry }) => {
    const initialForm = editing ? {
        name:             editing.name || "",
        category:         editing.category || "office",
        custom_category:  editing.custom_category || "",
        serial_number:    editing.serial_number || "",
        quantity:         editing.quantity != null ? String(editing.quantity) : "1",
        purchase_price:   editing.purchase_price != null ? String(editing.purchase_price) : "",
        purchase_date:    editing.purchase_date || "",
        warranty_till:    editing.warranty_till || "",
        vendor:           editing.vendor || "",
        warehouse:        editing.warehouse?.id != null ? String(editing.warehouse.id) : (editing.warehouse ? String(editing.warehouse) : ""),
        allocated_type:   editing.allocated_type || "unallocated",
        allocated_staff:  editing.allocated_staff?.id != null ? String(editing.allocated_staff.id) : (editing.allocated_staff ? String(editing.allocated_staff) : ""),
        allocated_member: editing.allocated_member?.id != null ? String(editing.allocated_member.id) : (editing.allocated_member ? String(editing.allocated_member) : ""),
        allocated_at:     editing.allocated_at || "",
        condition:        editing.condition || "working",
        notes:            editing.notes || "",
        invoice_file:     null,
        image:            null,
    } : { ...EMPTY_FORM };

    const [form, setForm]     = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const invoiceRef = useRef(null);
    const imageRef   = useRef(null);

    const staff     = lookups?.staff     || [];
    const members   = lookups?.members   || [];
    const warehouses = lookups?.warehouses || [];

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    // total value preview
    const totalVal = () => {
        const p = parseFloat(form.purchase_price);
        const q = parseInt(form.quantity, 10);
        if (!isNaN(p) && !isNaN(q) && p > 0 && q > 0) return p * q;
        return null;
    };

    const validate = () => {
        const errs = {};
        if (!form.name.trim())          errs.name           = "Asset name is required";
        if (!form.purchase_price || isNaN(parseFloat(form.purchase_price)) || parseFloat(form.purchase_price) <= 0)
                                        errs.purchase_price = "Valid price required (> 0)";
        if (!form.quantity || isNaN(parseInt(form.quantity, 10)) || parseInt(form.quantity, 10) < 1)
                                        errs.quantity       = "Quantity must be ≥ 1";
        if (form.category === "other" && !form.custom_category.trim())
                                        errs.custom_category = "Enter a category name";
        if (form.allocated_type === "staff" && !form.allocated_staff)
                                        errs.allocated_staff  = "Select a staff member";
        if (form.allocated_type === "member" && !form.allocated_member)
                                        errs.allocated_member = "Select a member";
        if (form.warranty_till && form.purchase_date && form.warranty_till < form.purchase_date)
                                        errs.warranty_till   = "Warranty date must be after purchase date";
        return errs;
    };

    const handleSubmit = async () => {
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setSaving(true);
        try {
            const fd = new FormData();
            const append = (k, v) => { if (v != null && v !== "") fd.append(k, v); };
            append("name",             form.name.trim());
            append("category",         form.category);
            if (form.category === "other") append("custom_category", form.custom_category.trim());
            append("serial_number",    form.serial_number.trim());
            append("quantity",         parseInt(form.quantity, 10));
            append("purchase_price",   parseFloat(form.purchase_price));
            append("purchase_date",    form.purchase_date);
            append("warranty_till",    form.warranty_till);
            append("vendor",           form.vendor.trim());
            append("warehouse",        form.warehouse);
            append("allocated_type",   form.allocated_type);
            if (form.allocated_type === "staff")  append("allocated_staff",  form.allocated_staff);
            if (form.allocated_type === "member") append("allocated_member", form.allocated_member);
            append("allocated_at",     form.allocated_at);
            append("condition",        form.condition);
            append("notes",            form.notes.trim());
            if (form.invoice_file) fd.append("invoice_file", form.invoice_file);
            if (form.image)        fd.append("image", form.image);

            if (editing) await updateAsset(editing.id, fd);
            else         await createAsset(fd);
            toast.success(editing ? "Asset updated ✅" : "Asset added ✅");
            onSaved();
            onClose();
        } catch (e) {
            const errsData = e?.response?.data;
            if (errsData && typeof errsData === "object" && !errsData.detail) {
                const fieldErrs = {};
                Object.entries(errsData).forEach(([k, v]) => {
                    fieldErrs[k] = Array.isArray(v) ? v.join(" ") : String(v);
                });
                setErrors(fieldErrs);
                toast.error("Please fix the errors below.");
            } else {
                toast.error(errsData?.detail || errsData?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    const iCls  = (k) => `w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white ${errors[k] ? "border-red-400" : "border-gray-300"}`;
    const errEl = (k) => errors[k] ? <p className="text-red-500 text-[11px] mt-1">{errors[k]}</p> : null;

    return (
        <Modal
            title={editing ? "Edit asset" : "Add asset"}
            subtitle="Register a physical asset owned by the FPO"
            onClose={onClose}
            wide
        >
            <div className="flex flex-col gap-3.5">
                {/* Name + Quantity */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Asset name <span className="text-red-500">*</span>
                        </label>
                        <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)}
                            placeholder="e.g. Weighing machine 500kg" className={iCls("name")} />
                        {errEl("name")}
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Quantity <span className="text-red-500">*</span>
                        </label>
                        <input type="number" min="1" step="1" value={form.quantity}
                            onChange={(e) => set("quantity", e.target.value)} className={iCls("quantity")} />
                        {errEl("quantity")}
                    </div>
                </div>
                <p className="text-[11px] text-gray-400 -mt-2">
                    Use quantity for identical items allocated together (e.g. 12 chairs to Office). If a batch needs to be split across different owners (e.g. 5 computers to different people), add them as separate rows with quantity 1 each.
                </p>

                {/* Category + Serial */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
                        <SearchableSelect
                            value={form.category}
                            onChange={(v) => set("category", v)}
                            options={CATEGORY_OPTIONS}
                            className={iCls("category")}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Serial number <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input type="text" value={form.serial_number}
                            onChange={(e) => set("serial_number", e.target.value)}
                            placeholder="e.g. WB-4471" className={iCls("serial_number")} />
                    </div>
                </div>

                {/* Custom category — shown only when category=other */}
                {form.category === "other" && (
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Category name <span className="text-red-500">*</span>
                        </label>
                        <input type="text" value={form.custom_category}
                            onChange={(e) => set("custom_category", e.target.value)}
                            placeholder="e.g. Irrigation equipment" className={iCls("custom_category")} />
                        {errEl("custom_category")}
                    </div>
                )}

                {/* Price + Purchase date + Warranty */}
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Purchase price / unit <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">₹</span>
                            <input type="number" min="0" step="0.01" value={form.purchase_price}
                                onChange={(e) => set("purchase_price", e.target.value)}
                                placeholder="32000" className={`${iCls("purchase_price")} pl-6`} />
                        </div>
                        {errEl("purchase_price")}
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Purchase date</label>
                        <input type="date" value={form.purchase_date}
                            onChange={(e) => set("purchase_date", e.target.value)} className={iCls("purchase_date")} />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Warranty till</label>
                        <input type="date" value={form.warranty_till}
                            onChange={(e) => set("warranty_till", e.target.value)} className={iCls("warranty_till")} />
                        {errEl("warranty_till")}
                    </div>
                </div>

                {/* Vendor + Warehouse */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Vendor <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input type="text" value={form.vendor}
                            onChange={(e) => set("vendor", e.target.value)}
                            placeholder="e.g. Ratlam Scales & Co." className={iCls("vendor")} />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Warehouse / Location</label>
                        <SearchableSelect
                            value={form.warehouse}
                            onChange={(v) => set("warehouse", v)}
                            placeholder="No specific location"
                            allowClear
                            options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
                            className={iCls("warehouse")}
                        />
                    </div>
                </div>

                {/* Allocation type tab buttons */}
                <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Allocation</label>
                    <div className="flex gap-2 flex-wrap">
                        {ALLOCATION_TABS.map((t) => (
                            <button
                                key={t.value}
                                type="button"
                                onClick={() => {
                                    set("allocated_type", t.value);
                                    set("allocated_staff", "");
                                    set("allocated_member", "");
                                }}
                                className={`px-3 py-2 text-xs font-semibold rounded-xl border transition-colors ${
                                    form.allocated_type === t.value
                                        ? "bg-primary text-white border-primary shadow"
                                        : "border-gray-300 text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1.5">
                        Staff and member lists only show people in this FPO — an asset can't be allocated outside it.
                    </p>
                </div>

                {/* Staff picker */}
                {form.allocated_type === "staff" && (
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Allocate to staff <span className="text-red-500">*</span>
                        </label>
                        <SearchableSelect
                            value={form.allocated_staff}
                            onChange={(v) => set("allocated_staff", v)}
                            placeholder="Select staff…"
                            searchPlaceholder="Search staff…"
                            options={staff.map((s) => ({ value: s.id, label: s.name }))}
                            className={iCls("allocated_staff")}
                            error={!!errors.allocated_staff}
                        />
                        {errEl("allocated_staff")}
                        <p className="text-[10px] text-gray-400 mt-1">Only this FPO's staff appear — schema-scoped to the FPO.</p>
                    </div>
                )}

                {/* Member picker */}
                {form.allocated_type === "member" && (
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Allocate to member <span className="text-red-500">*</span>
                        </label>
                        <SearchableSelect
                            value={form.allocated_member}
                            onChange={(v) => set("allocated_member", v)}
                            placeholder="Select member…"
                            searchPlaceholder="Search members…"
                            options={members.map((m) => ({ value: m.id, label: m.name }))}
                            className={iCls("allocated_member")}
                            error={!!errors.allocated_member}
                        />
                        {errEl("allocated_member")}
                    </div>
                )}

                {/* Allocated at date — show when not unallocated */}
                {form.allocated_type !== "unallocated" && (
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Allocated on</label>
                        <input type="date" value={form.allocated_at}
                            onChange={(e) => set("allocated_at", e.target.value)} className={iCls("allocated_at")} />
                    </div>
                )}

                {/* Condition (data_entry/field_staff can set on create but not update) */}
                {(!isDataEntry || !editing) && (
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Condition</label>
                        <SearchableSelect
                            value={form.condition}
                            onChange={(v) => set("condition", v)}
                            options={CONDITION_OPTIONS}
                            className={iCls("condition")}
                        />
                    </div>
                )}

                {/* Invoice / Image */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Invoice <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <button type="button" onClick={() => invoiceRef.current?.click()}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm border border-dashed border-gray-300 rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-colors text-gray-500">
                            📎 {form.invoice_file ? form.invoice_file.name : "Upload file"}
                        </button>
                        <input ref={invoiceRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                            onChange={(e) => set("invoice_file", e.target.files[0] || null)} />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Image <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <button type="button" onClick={() => imageRef.current?.click()}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm border border-dashed border-gray-300 rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-colors text-gray-500">
                            🖼️ {form.image ? form.image.name : "Upload image"}
                        </button>
                        <input ref={imageRef} type="file" accept="image/*" className="hidden"
                            onChange={(e) => set("image", e.target.files[0] || null)} />
                    </div>
                </div>

                {/* Notes */}
                <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Notes <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                        placeholder="Any remarks about condition, location, usage…"
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white resize-none" />
                </div>

                {/* Total value preview */}
                {totalVal() != null && (
                    <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-2.5 flex items-center justify-between">
                        <span className="text-xs text-green-700 font-medium">Total value (price × qty)</span>
                        <span className="font-bold text-green-800 text-sm">{fmtMoney(totalVal())}</span>
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2 border-t border-gray-200">
                    <button type="button" onClick={onClose}
                        className="flex-1 py-2.5 border border-gray-300 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors">
                        Cancel
                    </button>
                    <button type="button" onClick={handleSubmit} disabled={saving}
                        className="flex-1 py-2.5 bg-primary hover:bg-green-700 text-white font-semibold rounded-xl text-sm shadow-lg shadow-primary/20 transition-colors disabled:opacity-60">
                        {saving ? "Saving…" : editing ? "Update asset" : "Add asset"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────
// SUMMARY STAT CARD  (matches Procurement StatCard)
// ─────────────────────────────────────────────
const StatCard = ({ label, value, sub, highlight }) => (
    <div className={`rounded-2xl border shadow-sm px-5 py-4 flex flex-col gap-1 ${highlight ? "bg-amber-50 border-amber-200" : "bg-white border-gray-100"}`}>
        <p className={`text-[11px] font-semibold uppercase tracking-widest ${highlight ? "text-amber-600" : "text-gray-400"}`}>{label}</p>
        <p className={`text-2xl font-bold ${highlight ? "text-amber-700" : "text-gray-900"}`}>{value}</p>
        {sub && <p className={`text-[11px] ${highlight ? "text-amber-500" : "text-gray-400"}`}>{sub}</p>}
    </div>
);

// ─────────────────────────────────────────────
// CATEGORY FILTER TABS
// ─────────────────────────────────────────────
const CATEGORY_FILTERS = ["all", "office", "machinery", "vehicle", "furniture", "electronics", "other"];
const CATEGORY_LABELS_MAP = {
    all: "All", office: "Office", machinery: "Machinery",
    vehicle: "Vehicles", furniture: "Furniture", electronics: "Electronics", other: "Other",
};
const CONDITION_FILTERS = ["all", "working", "under_repair", "disposed"];
const CONDITION_FILTER_LABELS = {
    all: "All conditions", working: "Working",
    under_repair: "Under repair", disposed: "Disposed",
};
const PAGE_SIZE = 20;

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────
const AssetRegister = () => {
    const { canWrite, isAdmin, isDataEntry } = useRole();

    // list state
    const [rows, setRows]       = useState([]);
    const [total, setTotal]     = useState(0);
    const [page, setPage]       = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    // filters
    const [search, setSearch]           = useState("");
    const [catFilter, setCatFilter]     = useState("all");
    const [condFilter, setCondFilter]   = useState("all");
    const [allocFilter, setAllocFilter] = useState("all");

    // summary
    const [summary, setSummary] = useState(null);

    // lookups (staff / members / warehouses for the form)
    const [lookups, setLookups] = useState({ staff: [], members: [], warehouses: [] });

    // modals
    const [showAdd, setShowAdd]       = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [deletingRow, setDeletingRow] = useState(null);

    // ── Load summary ──
    const loadSummary = useCallback(async () => {
        try {
            const res = await getAssetSummary();
            setSummary(res?.data || null);
        } catch { /* silent */ }
    }, []);

    // ── Load lookups (once) ──
    useEffect(() => {
        getAssetLookups()
            .then((res) => {
                const d = res?.data || {};
                setLookups({
                    staff:      d.staff      || [],
                    members:    d.members    || [],
                    warehouses: d.warehouses || [],
                });
            })
            .catch(() => {});
    }, []);

    // ── Load asset list ──
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (search.trim())          params.search        = search.trim();
            if (catFilter !== "all")    params.category      = catFilter;
            if (condFilter !== "all")   params.condition     = condFilter;
            if (allocFilter !== "all")  params.allocated_type = allocFilter;
            const res = await getAssets(params);
            const data = res?.data;
            setRows(data?.results || []);
            setTotal(data?.count  || 0);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.response?.data?.message || "Unable to load assets.");
        } finally {
            setLoading(false);
        }
    }, [page, search, catFilter, condFilter, allocFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadSummary(); }, [loadSummary]);

    const handleSaved   = () => { load(); loadSummary(); };
    const handleDeleted = () => { load(); loadSummary(); };

    // ── Stat card values ──
    const stats = [
        {
            label: "Total assets",
            value: summary?.total_assets != null ? `${summary.total_assets} units` : "—",
            sub:   summary ? `${summary.total_line_items ?? "—"} line items · ${summary.total_categories ?? "—"} categories` : null,
        },
        {
            label: "Total value",
            value: summary?.total_value != null ? fmtMoney(summary.total_value) : "—",
            sub:   "price × quantity",
        },
        {
            label: "Allocated",
            value: summary?.allocated_count != null ? summary.allocated_count : "—",
            sub:   summary?.unallocated_count != null ? `${summary.unallocated_count} unallocated` : null,
        },
        {
            label: "Under repair",
            value: summary?.under_repair_count != null ? summary.under_repair_count : "—",
            sub:   summary?.warranty_expiring_count != null ? `${summary.warranty_expiring_count} warranty expiring` : null,
            highlight: summary?.under_repair_count > 0,
        },
    ];

    return (
        <div className="w-full pb-12">
            {/* Page header */}
            <div className="sticky top-0 z-20 bg-white border-b border-gray-200 -mx-4 -mt-4 mb-5 px-4 sm:px-6 py-4 flex items-center justify-between">
                <div>
                    <h1 className="text-gray-900 text-lg font-bold">Asset Register</h1>
                    <p className="text-gray-400 text-xs mt-0.5">Furniture, electronics, machinery & vehicles owned by the FPO</p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Search */}
                    <div className="relative hidden sm:block">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Search name, code, serial…"
                            className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white w-52"
                        />
                    </div>
                    {canWrite && (
                        <button
                            id="add-asset-btn"
                            type="button"
                            onClick={() => { setEditingRow(null); setShowAdd(true); }}
                            className="flex items-center gap-2 bg-primary hover:bg-green-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-lg shadow-primary/20"
                        >
                            <FaPlus size={11} /> Add asset
                        </button>
                    )}
                </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                {stats.map((s) => (
                    <StatCard key={s.label} label={s.label} value={s.value} sub={s.sub} highlight={s.highlight} />
                ))}
            </div>

            {/* Category filter tabs */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    {/* Category pills */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {CATEGORY_FILTERS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => { setCatFilter(c); setPage(1); }}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                                    catFilter === c
                                        ? "bg-primary text-white shadow"
                                        : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                {c !== "all" && <span className="mr-1">{CATEGORY_ICONS[c]}</span>}
                                {CATEGORY_LABELS_MAP[c]}
                            </button>
                        ))}
                    </div>
                    {/* Condition filter */}
                    <div className="w-44">
                        <SearchableSelect
                            value={condFilter}
                            onChange={(v) => { setCondFilter(v); setPage(1); }}
                            options={CONDITION_FILTERS.map((c) => ({ value: c, label: CONDITION_FILTER_LABELS[c] }))}
                            className="px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white text-gray-600"
                        />
                    </div>
                </div>

                {/* Mobile search */}
                <div className="relative sm:hidden mb-4">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        placeholder="Search name, code, serial…"
                        className="w-full pl-8 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white"
                    />
                </div>

                {/* Table */}
                {loading ? <Spinner label="Loading assets…" /> : error ? (
                    <ErrState msg={error} onRetry={load} />
                ) : rows.length === 0 ? (
                    <Empty label="No assets found" />
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        {["ASSET · CODE", "CATEGORY", "QTY", "ALLOCATED TO", "TOTAL VALUE", "CONDITION", ""].map((h) => (
                                            <th key={h} className="py-2 px-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap first:pl-0 last:pr-0">
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {rows.map((row) => {
                                        const categoryIcon = CATEGORY_ICONS[row.category] || "📦";
                                        const catLabel     = row.category_display || row.category || "—";
                                        const totalValue   = row.total_value != null
                                            ? fmtMoney(row.total_value)
                                            : fmtMoney(parseFloat(row.purchase_price || 0) * parseInt(row.quantity || 1, 10));
                                        const serialStr    = row.serial_number ? `SN ${row.serial_number}` : "no serial";
                                        const warrantyExp  = row.warranty_expiring_soon;
                                        const underWarranty = row.is_under_warranty;

                                        return (
                                            <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                                                {/* Asset name + code */}
                                                <td className="py-3 px-3 pl-0 whitespace-nowrap">
                                                    <div className="flex items-center gap-2.5">
                                                        {row.image ? (
                                                            <img src={row.image} alt={row.name}
                                                                className="w-8 h-8 rounded-lg object-cover flex-shrink-0 border border-gray-100" />
                                                        ) : (
                                                            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-base">
                                                                {categoryIcon}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <p className="font-semibold text-gray-800 text-xs">{row.name}</p>
                                                            <p className="text-[10px] text-gray-400">
                                                                {row.asset_code || "—"} · {serialStr}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Category */}
                                                <td className="py-3 px-3 whitespace-nowrap">
                                                    <span className="text-xs text-gray-700">{catLabel}</span>
                                                </td>

                                                {/* Qty */}
                                                <td className="py-3 px-3 whitespace-nowrap text-xs text-gray-700">
                                                    {row.quantity}
                                                </td>

                                                {/* Allocated to */}
                                                <td className="py-3 px-3 whitespace-nowrap">
                                                    <AllocBadge row={row} />
                                                </td>

                                                {/* Total value */}
                                                <td className="py-3 px-3 whitespace-nowrap">
                                                    <span className="font-bold text-gray-800 text-xs">{totalValue}</span>
                                                </td>

                                                {/* Condition + warranty badge */}
                                                <td className="py-3 px-3 whitespace-nowrap">
                                                    <div className="flex flex-col gap-1">
                                                        <ConditionPill condition={row.condition} />
                                                        {underWarranty && warrantyExp && (
                                                            <span className="text-[9px] font-bold text-amber-600">⚠ Warranty expiring</span>
                                                        )}
                                                        {underWarranty && !warrantyExp && (
                                                            <span className="text-[9px] text-green-500 flex items-center gap-0.5"><FaShieldAlt size={8} /> Warranty</span>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Actions */}
                                                <td className="py-3 px-3 pr-0 whitespace-nowrap">
                                                    <div className="flex items-center gap-1.5 justify-end">
                                                        {canWrite && (
                                                            <button
                                                                onClick={() => { setEditingRow(row); setShowAdd(true); }}
                                                                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
                                                                title="Edit"
                                                            >
                                                                <FaEdit size={11} />
                                                            </button>
                                                        )}
                                                        {isAdmin && (
                                                            <button
                                                                onClick={() => setDeletingRow(row)}
                                                                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Decommission"
                                                            >
                                                                <FaTrash size={11} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPage={setPage} />
                        <p className="text-[11px] text-gray-400 mt-2">
                            Showing {rows.length} of {total} line items
                        </p>
                    </>
                )}
            </div>

            {/* Modals */}
            <AnimatePresence>
                {showAdd && (
                    <AssetFormModal
                        editing={editingRow}
                        lookups={lookups}
                        isDataEntry={isDataEntry}
                        onClose={() => { setShowAdd(false); setEditingRow(null); }}
                        onSaved={handleSaved}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {deletingRow && (
                    <DeleteModal
                        asset={deletingRow}
                        onClose={() => setDeletingRow(null)}
                        onDeleted={handleDeleted}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default AssetRegister;
